import { useState } from "react";
import type { Locale } from "../../i18n/dictionary";
import { getDictionary } from "../../i18n/dictionary";
import "../../components/portal.css";
import { sendMagicLink, adminRedirectUrl } from "./sendMagicLink";

function ZooyloLogo({ size = 34 }: { size?: number }) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
      <rect x="2" y="2" width="44" height="44" rx="14" fill="var(--brand)" />
      <circle cx="18" cy="20" r="4" fill="#FBF7F0" />
      <circle cx="30" cy="20" r="4" fill="#FBF7F0" />
      <path d="M16 30 Q24 37 32 30" stroke="#FBF7F0" strokeWidth="3" fill="none" strokeLinecap="round" />
    </svg>
  );
}

// ---- AdminSignIn -------------------------------------------------------------

export interface AdminSignInProps {
  locale: Locale;
  setLocale: (l: Locale) => void;
  onSent: (email: string) => void;
  /** Called to show the "not a member" screen (org gate). */
  onNotOrg?: () => void;
}

/**
 * Admin-portal sign-in screen.
 * Work email, org-gated. Warm peach notices, never red (05_PORTAL_DESIGN.md §3, §4).
 */
export function AdminSignIn({ locale, setLocale, onSent, onNotOrg }: AdminSignInProps) {
  const dict = getDictionary(locale);
  const t = dict.admin.signIn;
  const c = dict.common;
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSend() {
    if (sending || !email.trim()) return;
    setSending(true);
    setError(null);
    const { error: err } = await sendMagicLink(email.trim(), adminRedirectUrl());
    setSending(false);
    if (err) {
      setError(err);
    } else {
      onSent(email.trim());
    }
  }

  return (
    <div
      className="zp-root"
      style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, position: "relative", overflow: "hidden", fontFamily: "var(--font-body)", background: "var(--bg)", color: "var(--ink)" }}
    >
      <div style={{ position: "absolute", top: -160, insetInlineStart: -120, width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, var(--lilac-tint), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -180, insetInlineEnd: -120, width: 560, height: 560, borderRadius: "50%", background: "radial-gradient(circle, var(--brand-tint), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />

      {/* Language switcher */}
      <div style={{ position: "absolute", top: 24, insetInlineEnd: 28, display: "flex", gap: 3, padding: 4, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-pill)", boxShadow: "var(--sh-1)" }}>
        <button className="zseg" data-on={String(locale === "en")} onClick={() => setLocale("en")} style={{ border: 0, background: "transparent", color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13, padding: "8px 13px", borderRadius: "var(--r-pill)", cursor: "pointer" }}>English</button>
        <button className="zseg" data-on={String(locale === "ms")} onClick={() => setLocale("ms")} style={{ border: 0, background: "transparent", color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13, padding: "8px 13px", borderRadius: "var(--r-pill)", cursor: "pointer" }}>Bahasa Melayu</button>
      </div>

      <div style={{ position: "relative", width: "100%", maxWidth: 440 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28, justifyContent: "center" }}>
          <ZooyloLogo size={40} />
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 28, color: "var(--brand)", letterSpacing: "-.02em" }}>zooylo</span>
        </div>

        <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-xl)", boxShadow: "var(--sh-1)", padding: "36px 34px" }}>
          <span style={{ fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 13, letterSpacing: ".14em", textTransform: "uppercase" as const, color: "var(--ink-faint)" }}>Organisation portal</span>
          <h1 style={{ fontSize: 26, marginTop: 12, color: "var(--ink-deep)", margin: "12px 0 0" }}>{t.title}</h1>
          <p style={{ color: "var(--ink-soft)", marginTop: 10, fontSize: 16, lineHeight: 1.6, margin: "10px 0 0" }}>{t.body}</p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 24 }}>
            <label htmlFor="adm-email" style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)", letterSpacing: ".02em" }}>{t.emailLabel}</label>
            <input
              id="adm-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void handleSend()}
              type="email"
              autoComplete="work email"
              placeholder="you@centre.org"
              style={{ minHeight: 52, padding: "14px 16px", fontSize: 17, fontFamily: "var(--font-body)", color: "var(--ink)", background: "var(--surface)", border: "2px solid var(--line-strong)", borderRadius: "var(--r-md)", width: "100%", outline: "none" }}
            />
            <span style={{ fontSize: 13.5, color: "var(--ink-faint)" }}>{t.emailHint}</span>
          </div>

          {/* Warm peach error notice — never red */}
          {error && (
            <div
              role="alert"
              style={{ background: "var(--peach-tint)", border: "1px solid var(--peach)", borderRadius: "var(--r-md)", padding: "12px 16px", marginTop: 16, color: "var(--peach-deep)", fontSize: 14.5, lineHeight: 1.5 }}
            >
              {error}
            </div>
          )}

          <button
            onClick={() => void handleSend()}
            disabled={sending}
            style={{ marginTop: 22, width: "100%", minHeight: 54, border: 0, borderRadius: "var(--r-pill)", background: "var(--brand)", color: "#fff", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 17, cursor: sending ? "wait" : "pointer", boxShadow: "var(--sh-1)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
          >
            {sending && (
              <span style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.5)", borderTopColor: "#fff", borderRadius: "50%", animation: "zspin .8s linear infinite" }} />
            )}
            {sending ? c.sending : c.sendMagicLink}
          </button>

          {/* Dev / future: org-gate check triggers this */}
          {onNotOrg && (
            <div style={{ marginTop: 14, textAlign: "center" as const }}>
              <button
                onClick={onNotOrg}
                style={{ background: "transparent", border: 0, color: "var(--ink-faint)", fontFamily: "var(--font-body)", fontSize: 13.5, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
              >
                Not a member of an organisation?
              </button>
            </div>
          )}
        </div>

        <p style={{ textAlign: "center", color: "var(--ink-faint)", fontSize: 13.5, marginTop: 18 }}>{t.guardrail}</p>
      </div>
    </div>
  );
}
